import {useMemo, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {Activity, RefreshCw} from 'lucide-react'
import {useQueryClient} from '@tanstack/react-query'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {Card, CardContent} from '@/components/ui/card'
import {Alert, AlertDescription} from '@/components/ui/alert'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {Badge} from '@/components/ui/badge'
import {Button} from '@/components/ui/button'
import {Pagination} from '@/components/ui/pagination'
import {Skeleton} from '@/components/ui/skeleton'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {ErrorBanner} from '@/components/ui/error-banner'
import {EmptyState} from '@/components/shared/EmptyState'
import {MutedText} from '@/components/shared/MutedText'
import {InlineRow} from '@/components/shared/InlineRow'
import {Stack} from '@/components/shared/Stack'
import {MonoText} from '@/components/shared/MonoText'
import {SmallText} from '@/components/shared/SmallText'
import {StatusText} from '@/components/shared/StatusText'
import {useServerPagination} from '@/hooks/use-server-pagination'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {ObservabilityControllerTracesEndpoint} from '@/types/endpoint-contracts'
import {useObservabilityHealth, useTraceList} from '@/hooks/use-observability'
import {formatTimestamp, formatRelativeTime} from '@/lib/date-utils'
import {routes} from '@/lib/routes'
import {WriterStatus} from '@/types/observability'
import {PageLayout} from '@/components/shared/PageLayout'

// WHY: spec §4.8.3 — audit-class minimum-q threshold. The backend rejects
// shorter values with HTTP 400 + VA0001_MIN_LENGTH; the bar enforces the
// same client-side so the user gets instant feedback instead of an error toast.
const INSPECTOR_Q_MIN_CHARS = 3

/**
 * Request Inspector list page — Phase C.6 unified search migration
 * (issue 342.11). The page lists distinct request sessions; the underlying
 * {@code RequestTraceSpan} entity is the audit-class exception page in
 * spec §8.5 — HUGE volume, only the human-readable {@code operationName}
 * and {@code httpUrl} fields participate in {@code q}, and the diagnostic
 * columns ({@code sqlFingerprint}, {@code sqlStatement}, {@code errorCode},
 * {@code errorMessage}, {@code traceId}, {@code sessionId}, {@code actionId},
 * {@code correlationId}, {@code e2eRunId}, {@code e2eTestClass},
 * {@code e2eTestMethod}, {@code attributes}, {@code httpMethod}) are
 * intentionally NOT searched — they remain reachable via the typed exact-match
 * filters in the bar's filter panel and on the row detail page. The bar
 * enforces the 3-character minimum spec §4.8.3 client-side; the inline scope
 * hint explains the search surface to the operator.
 */
export function RequestInspector() {
    const i18n = useI18n()
    usePageTitle(i18n.requestInspector.pageTitle)
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    // WHY: the controller is @SortableFields(value = {}, ...) — no sort paths
    // are exposed, so `defaultSortBy` is the empty string. The typed overload
    // constrains defaultSortBy to keys-of-sort, which is `never` here; cast
    // through `as never` is the documented escape hatch for this shape.
    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: ObservabilityControllerTracesEndpoint,
        defaultSortBy: '' as never,
        sortable: false,
    })

    const [showMinQHint, setShowMinQHint] = useState(false)

    const hasActiveFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
    }, [searchAndFilters])

    const {data, isLoading, error} = useTraceList(queryString)
    const {data: health} = useObservabilityHealth()

    const totalItems = data?.totalElements ?? 0
    const totalPages = data?.totalPages ?? 0

    const isWriterOffline = health && health.writerStatus !== WriterStatus.RUNNING

    const handleRefresh = () => {
        queryClient.invalidateQueries({queryKey: ['observability']})
    }

    const isEmpty = !isLoading && !error && data && data.totalElements === 0 && !hasActiveFilters

    return (
        <PageLayout
            variant="dashboard"
            icon={CONCEPT_ICON.requestInspector}
            title={i18n.requestInspector.title as string}
            description={i18n.requestInspector.description as string}
            count={totalItems}
            data-testid="request-inspector-page"
        >
            {isWriterOffline && (
                <Alert variant="destructive" className="mb-4" data-testid="writer-offline-alert">
                    <Activity className="h-4 w-4"/>
                    <AlertDescription>{i18n.requestInspector.writerOffline}</AlertDescription>
                </Alert>
            )}

            {isEmpty && (
                <Card className="mb-6" data-testid="empty-state-card">
                    <CardContent className="pt-6">
                        <EmptyState
                            icon={Activity}
                            title={String(i18n.requestInspector.noSessions)}
                            description=""
                        />
                    </CardContent>
                </Card>
            )}

            <Card data-testid="request-inspector-list">
                <CardContent className="pt-6">
                    <Stack gap="md">
                        <Stack gap="xs">
                            <InlineRow gap="sm" data-testid="page-layout-filters">
                                <UnifiedSearchBar
                                    contract={ObservabilityControllerTracesEndpoint}
                                    value={searchAndFilters}
                                    onChange={setSearchAndFilters}
                                    placeholder={i18n.requestInspector.searchPlaceholder as string}
                                    qMinChars={INSPECTOR_Q_MIN_CHARS}
                                    onMinQHintChange={setShowMinQHint}
                                />
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={handleRefresh}
                                    data-testid="btn-refresh"
                                    title={i18n.requestInspector.refresh as string}
                                >
                                    <RefreshCw className="h-4 w-4"/>
                                </Button>
                            </InlineRow>
                            {showMinQHint && (
                                <StatusText
                                    tone="warning"
                                    size="xs"
                                    data-testid="inspector-min-q-hint"
                                >
                                    {i18n.requestInspector.minQHint}
                                </StatusText>
                            )}
                            <StatusText
                                tone="info"
                                size="xs"
                                muted
                                data-testid="inspector-search-scope-hint"
                            >
                                {i18n.requestInspector.searchScopeHint}
                            </StatusText>
                        </Stack>
                        {error ? (
                            <ErrorBanner error={error as Error}/>
                        ) : (
                            <Table data-testid="traces-table">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead data-testid="col-session-id">
                                            {i18n.requestInspector.columns.sessionId}
                                        </TableHead>
                                        <TableHead data-testid="col-span-count" className="w-20 text-right">
                                            {i18n.requestInspector.columns.spanCount}
                                        </TableHead>
                                        <TableHead data-testid="col-error-count" className="w-20 text-right">
                                            {i18n.requestInspector.columns.errorCount}
                                        </TableHead>
                                        <TableHead data-testid="col-total-duration" className="w-28 text-right">
                                            {i18n.requestInspector.columns.totalDuration}
                                        </TableHead>
                                        <TableHead data-testid="col-earliest-started-at" className="w-36">
                                            {i18n.requestInspector.columns.earliestStartedAt}
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {isLoading ? (
                                        Array.from({length: 5}).map((_, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell><Skeleton className="h-4 w-48"/></TableCell>
                                                <TableCell><Skeleton className="h-4 w-10"/></TableCell>
                                                <TableCell><Skeleton className="h-4 w-10"/></TableCell>
                                                <TableCell><Skeleton className="h-4 w-16"/></TableCell>
                                                <TableCell><Skeleton className="h-4 w-24"/></TableCell>
                                            </TableRow>
                                        ))
                                    ) : data?.content.length === 0 ? (
                                        <TableEmptyState
                                            colSpan={5}
                                            hasFilters={hasActiveFilters}
                                            matchMessage={i18n.requestInspector.noSessionsMatch as string}
                                            emptyMessage={i18n.requestInspector.noSessions as string}
                                        />
                                    ) : (
                                        data?.content.map((item) => (
                                            <TableRow
                                                key={item.sessionId}
                                                className="cursor-pointer hover:bg-muted/50"
                                                onClick={() => navigate(routes.admin.requestInspector.detail(item.sessionId))}
                                                data-testid={`trace-row-${item.sessionId}`}
                                            >
                                                <TableCell>
                                                    <SmallText className="font-medium">
                                                        {item.e2eTestClass
                                                            ? <MonoText size="sm">{item.e2eTestClass}{item.e2eTestMethod ? `.${item.e2eTestMethod}` : ''}</MonoText>
                                                            : formatRelativeTime(item.earliestStartedAt)
                                                        }
                                                    </SmallText>
                                                    <MonoText size="xs" muted block className="mt-0.5" title={item.sessionId}>
                                                        {item.sessionId}
                                                    </MonoText>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <Badge variant="outline">{item.spanCount}</Badge>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {item.errorCount > 0 ? (
                                                        <Badge variant="destructive">{item.errorCount}</Badge>
                                                    ) : (
                                                        <Badge variant="outline">{item.errorCount}</Badge>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {/* eslint-disable-next-line i18next/no-literal-string -- 'ms' is a unit suffix, technical not translated */}
                                                    <MutedText size="sm">{item.totalDurationMs}ms</MutedText>
                                                </TableCell>
                                                <TableCell className="text-sm text-muted-foreground" title={formatTimestamp(item.earliestStartedAt)}>
                                                    {formatRelativeTime(item.earliestStartedAt)}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        )}
                        {totalItems > 0 && (
                            <Pagination
                                page={page}
                                pageSize={pageSize}
                                totalItems={totalItems}
                                totalPages={totalPages}
                                onPageChange={setPage}
                                onPageSizeChange={setPageSize}
                                pageSizeOptions={pageSizeOptions}
                            />
                        )}
                    </Stack>
                </CardContent>
            </Card>
        </PageLayout>
    )
}
